import { useCallback, useEffect, useState } from "react";
import { ArrowDownLeft, ArrowUpRight, CheckCheck, Globe2, Loader2, Plus } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Chip, DecisionChip, Empty, Kpi, Loading, PageHeader, Panel, SeverityChip, Table, Td, Th } from "@/components/common/Chrome";
import { refOf, rowClick, useRowDetail } from "@/components/common/DetailSheet";
import { StatementImport } from "@/components/finance/StatementImport";
import { api, apiError, decisionPayload } from "@/lib/api";
import { CostCentres } from "@/components/settings/CostCentres";
import { useAuth } from "@/context/AuthContext";
import { money, shortDate } from "@/lib/format";

const BLANK = {
  entry_date: new Date().toISOString().slice(0, 10), fund_id: "", account_code: "5200",
  account_name: "Charitable Expenditure", direction: "debit", amount: 100000, currency: "INR",
  fx_rate: 1, cross_border: false, description: "", purpose_code: "education_supplies",
  programme_code: "", campaign_code: "", cost_centre_code: "", work_item_id: "", work_task: "",
};

export default function Finance() {
  const { can } = useAuth();
  const [rows, setRows] = useState(null);
  const detail = useRowDetail();
  const [funds, setFunds] = useState([]);
  const [form, setForm] = useState(BLANK);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState("");
  const [blockDecision, setBlockDecision] = useState(null);
  const [tags, setTags] = useState({ programmes: [], events: [], work_items: [] });
  const [centres, setCentres] = useState([]);
  const selectedWork = tags.work_items.find((w) => w.id === form.work_item_id);

  useEffect(() => {
    api.get("/expense-claims/meta")
      .then((r) => {
        setTags({ programmes: r.data.programmes, events: r.data.events,
                  work_items: r.data.work_items });
        setCentres(r.data.cost_centres || []);
      })
      .catch(() => setTags({ programmes: [], events: [], work_items: [] }));
  }, []);

  const load = useCallback(async () => {
    const [l, f] = await Promise.all([api.get("/charity/ledger"), api.get("/charity/funds")]);
    setRows(l.data);
    setFunds(f.data);
  }, []);

  useEffect(() => { load().catch(() => setRows([])); }, [load]);

  const save = async () => {
    setBusy("save");
    setBlockDecision(null);
    try {
      const { data } = await api.post("/charity/ledger", form);
      toast.success(`Posted ${money(data.entry.amount, data.entry.currency)} · decision ${data.decision.decision}`);
      setOpen(false);
      await load();
    } catch (e) {
      const dec = decisionPayload(e);
      if (dec) {
        setBlockDecision(dec);
        toast.error("Posting blocked by the Regulatory Decision Engine");
      } else {
        toast.error(apiError(e));
      }
    }
    setBusy("");
  };

  const reconcile = async (id) => {
    setBusy(id);
    try {
      await api.post(`/charity/ledger/${id}/reconcile`);
      toast.success("Entry marked reconciled");
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
    setBusy("");
  };

  if (!rows) return <Loading label="Loading general ledger" />;
  const income = rows.filter((r) => r.direction === "credit").reduce((a, r) => a + r.base_amount, 0);
  const spend = rows.filter((r) => r.direction === "debit").reduce((a, r) => a + r.base_amount, 0);
  const crossBorder = rows.filter((r) => r.cross_border).length;
  const unreconciled = rows.filter((r) => !r.reconciled).length;

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="SCR-008 · Finance Workspace"
        title="Multi-currency ledger with cross-border scrutiny"
        description="Every debit against a fund is evaluated for restriction breach and overspend before it posts. Foreign-contribution entries are flagged and routed for regulatory review."
        actions={can("finance", "write") && (
          <>
          <StatementImport onReconciled={load} />
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setBlockDecision(null); }}>
            <DialogTrigger asChild>
              <Button data-testid="new-entry-button" className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                <Plus className="mr-1.5 h-4 w-4" /> Post entry
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-xl border-border bg-card">
              <DialogHeader>
                <DialogTitle>Post a ledger entry</DialogTitle>
                <DialogDescription className="text-xs text-muted-foreground">
                  Try a purpose code outside the fund's permitted list to see a hard block.
                </DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <F label="Fund" full>
                  <Select value={form.fund_id} onValueChange={(v) => setForm({ ...form, fund_id: v })}>
                    <SelectTrigger data-testid="entry-fund-select" className="bg-muted/40">
                      <SelectValue placeholder="Select fund" />
                    </SelectTrigger>
                    <SelectContent>
                      {funds.map((f) => (
                        <SelectItem key={f.id} value={f.id}>
                          {f.code} · {money(f.balance, f.currency, true)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </F>
                <F label="Direction">
                  <Select value={form.direction} onValueChange={(v) => setForm({ ...form, direction: v })}>
                    <SelectTrigger data-testid="entry-direction-select" className="bg-muted/40"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="debit">debit (expenditure)</SelectItem>
                      <SelectItem value="credit">credit (income)</SelectItem>
                    </SelectContent>
                  </Select>
                </F>
                <F label="Amount">
                  <Input type="number" value={form.amount}
                         onChange={(e) => setForm({ ...form, amount: Number(e.target.value) })}
                         data-testid="entry-amount-input" className="bg-muted/40 font-mono" />
                </F>
                <F label="Purpose code">
                  <Input value={form.purpose_code} onChange={(e) => setForm({ ...form, purpose_code: e.target.value })}
                         data-testid="entry-purpose-input" className="bg-muted/40 font-mono text-xs" />
                </F>
                <F label="Entry date">
                  <Input type="date" value={form.entry_date}
                         onChange={(e) => setForm({ ...form, entry_date: e.target.value })}
                         data-testid="entry-date-input" className="bg-muted/40 font-mono" />
                </F>
                <F label="Programme (so it shows in programme spend)">
                  <Select value={form.programme_code || "none"}
                          onValueChange={(v) => setForm({ ...form, programme_code: v === "none" ? "" : v })}>
                    <SelectTrigger data-testid="entry-programme-select" className="bg-muted/40">
                      <SelectValue placeholder="Not against a programme" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Not against a programme</SelectItem>
                      {tags.programmes.map((p) => (
                        <SelectItem key={p.code} value={p.code}>{p.name}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </F>
                <F label="Campaign or event">
                  <Select value={form.campaign_code || "none"}
                          onValueChange={(v) => setForm({ ...form, campaign_code: v === "none" ? "" : v })}>
                    <SelectTrigger data-testid="entry-campaign-select" className="bg-muted/40">
                      <SelectValue placeholder="Not against an event" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Not against an event</SelectItem>
                      {tags.events.map((e) => (
                        <SelectItem key={e.code} value={e.code}>{e.name}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </F>
                <F label="Everyday cost centre">
                  <Select value={form.cost_centre_code || "none"}
                          onValueChange={(v) => setForm({ ...form, cost_centre_code: v === "none" ? "" : v })}>
                    <SelectTrigger data-testid="entry-cost-centre-select" className="bg-muted/40">
                      <SelectValue placeholder="Not a running cost" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Not a running cost</SelectItem>
                      {centres.map((c) => (
                        <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </F>
                <F label="Work item on the board">
                  <Select value={form.work_item_id || "none"}
                          onValueChange={(v) => setForm({ ...form, work_item_id: v === "none" ? "" : v,
                                                          work_task: "" })}>
                    <SelectTrigger data-testid="entry-work-select" className="bg-muted/40">
                      <SelectValue placeholder="Optional" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Not linked to the board</SelectItem>
                      {tags.work_items.map((w) => (
                        <SelectItem key={w.id} value={w.id}>{w.work_id} · {w.title}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </F>
                <F label="Task on that item">
                  <Select value={form.work_task || "none"} disabled={!selectedWork}
                          onValueChange={(v) => setForm({ ...form, work_task: v === "none" ? "" : v })}>
                    <SelectTrigger data-testid="entry-task-select" className="bg-muted/40">
                      <SelectValue placeholder="Optional" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No particular task</SelectItem>
                      {(selectedWork?.tasks || []).map((t) => (
                        <SelectItem key={t.index} value={t.name}>{t.name}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </F>
                <F label="Description" full>
                  <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                         data-testid="entry-description-input" className="bg-muted/40" />
                </F>
                <div className="col-span-2 flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Cross-border transaction</span>
                  <Switch checked={form.cross_border} onCheckedChange={(v) => setForm({ ...form, cross_border: v })}
                          data-testid="entry-crossborder-toggle" />
                </div>
              </div>

              {blockDecision && (
                <div className="rounded border border-red-500/30 bg-red-500/10 p-3" data-testid="entry-block-banner">
                  <div className="flex items-center gap-2">
                    <DecisionChip decision={blockDecision.decision} />
                    <span className="text-xs text-red-700 dark:text-red-300">Posting rejected — nothing was written to the ledger</span>
                  </div>
                  <div className="mt-2 space-y-1">
                    {blockDecision.triggered.map((t) => (
                      <div key={t.rule_key} className="flex items-start gap-2 text-[11px]">
                        <SeverityChip severity={t.effective_severity} />
                        <div>
                          <span className="font-mono text-primary">{t.rule_key}</span>
                          <span className="ml-2 text-muted-foreground">{t.title}</span>
                          {t.remediation && <div className="text-emerald-600 dark:text-emerald-400/80">{t.remediation}</div>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <Button onClick={save} disabled={busy === "save"} data-testid="entry-save-button"
                      className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                {busy === "save" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Post entry
              </Button>
            </DialogContent>
          </Dialog>
          </>
        )}
      />

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
        <Kpi testId="kpi-ledger-income" label="Total credits" value={money(income, "INR", true)} accent="emerald" icon={ArrowDownLeft} />
        <Kpi testId="kpi-ledger-spend" label="Total debits" value={money(spend, "INR", true)} accent="violet" icon={ArrowUpRight} />
        <Kpi testId="kpi-ledger-crossborder" label="Cross-border entries" value={crossBorder} accent="amber" icon={Globe2} />
        <Kpi testId="kpi-ledger-unreconciled" label="Unreconciled" value={unreconciled} accent="sky" icon={CheckCheck} />
      </div>

      <CostCentres />

      <Panel testId="panel-ledger" kicker="General ledger" title="Postings">
        <div className="max-h-[620px] overflow-y-auto">
          <Table testId="ledger-table"
                 head={<><Th>Date</Th><Th>Account</Th><Th>Fund</Th><Th>Description</Th><Th>Dr/Cr</Th><Th>Amount</Th><Th>Base (INR)</Th><Th>Flags</Th><Th /></>}>
            {rows.length === 0 && <Empty colSpan={9} label="No ledger entries" />}
            {rows.map((r) => (
              <tr key={refOf(r)} className="row-hover cursor-pointer" onClick={rowClick(() => detail.show(r, refOf(r)))} data-testid={`ledger-row-${r.id}`}>
                <Td className="whitespace-nowrap font-mono text-[11px]">{shortDate(r.entry_date)}</Td>
                <Td>
                  <div className="font-mono text-[11px] text-primary">{r.account_code}</div>
                  <div className="text-[11px] text-muted-foreground">{r.account_name}</div>
                </Td>
                <Td className="font-mono text-[11px]">{r.fund_code || "—"}</Td>
                <Td className="max-w-[240px] truncate text-muted-foreground">{r.description}</Td>
                <Td>
                  <Chip className={r.direction === "credit"
                    ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
                    : "border-violet-500/30 bg-violet-500/10 text-violet-700 dark:text-violet-300"}>{r.direction}</Chip>
                </Td>
                <Td className="font-mono">{money(r.amount, r.currency)}</Td>
                <Td className="font-mono text-muted-foreground">{money(r.base_amount, "INR", true)}</Td>
                <Td>
                  <div className="flex flex-wrap gap-1">
                    {r.cross_border && <Chip className="border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300">cross-border</Chip>}
                    {r.reconciled
                      ? <Chip className="border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300">reconciled</Chip>
                      : <Chip className="border-border bg-muted text-muted-foreground">open</Chip>}
                  </div>
                </Td>
                <Td>
                  {!r.reconciled && can("finance", "write") && (
                    <Button size="sm" variant="outline" onClick={() => reconcile(r.id)} disabled={busy === r.id}
                            data-testid={`reconcile-${r.id}`}
                            className="h-7 rounded-full border-border px-2.5 text-[10px]">
                      {busy === r.id ? <Loader2 className="h-3 w-3 animate-spin" /> : "Reconcile"}
                    </Button>
                  )}
                </Td>
              </tr>
            ))}
          </Table>
        </div>
      </Panel>

      {detail.sheet}
    </div>
  );
}

const F = ({ label, children, full }) => (
  <div className={`space-y-1.5 ${full ? "col-span-2" : ""}`}>
    <Label className="text-[11px] text-muted-foreground">{label}</Label>
    {children}
  </div>
);
